import Button from '@components/atoms/button'
import BookFreeClassButton from '@components/common/book-free-class-btn'
import { FastlyImage } from '@components/common/image'
import Link from 'next/link'
import { INTERNAL_LINKS_DATA } from '@lib/data/footer'
import { LinguiJSMessageDescriptor } from '@lib/types/common'
import MsgTrans from '@components/common/i18n/message-trans'
import { msgT } from '@lib/utils/i18n'

interface Course {
  label: LinguiJSMessageDescriptor
  description?: string
  image?: string
  url: string
}

const HOVER_BG = [
  'hover:bg-orange-100 hover:border-orange',
  'hover:bg-orange2-50 hover:border-orange2-400 hover:bg-opacity-75',
  'hover:bg-grey-200 hover:border-grey',
  'hover:bg-blue-50 hover:border-blue-300 hover:bg-opacity-50',
  'hover:bg-red-50 hover:border-red-200',
  'hover:bg-yellow-100 hover:bg-opacity-50 hover:border-yellow-200',
  'hover:bg-violet-50 hover:border-violet-100',
  'hover:bg-green-50 hover:border-green-200 hover:bg-opacity-50',
]

// Add description and image to the courses from INTERNAL_LINKS_DATA
const COURSES: Course[] = INTERNAL_LINKS_DATA.BESTSELLING_COURSE.slice(
  0,
  10
).map((course, index) => {
  const descriptions = [
    'Master the fundamentals of AI and machine learning',
    'Learn coding with visual blocks and AI concepts',
    'Become a Python programming expert',
    'Create your own games from scratch',
    'Build modern, responsive websites',
    'Create mobile apps for iOS and Android',
    'Analyze data and build AI models',
    'Design and code games on Roblox',
    'Prepare for the AP CS exam',
    'Master competitive programming',
  ]

  const images = [
    '/images/about-us/courses/ai-ml.png',
    '/images/about-us/courses/scratch.png',
    '/images/about-us/courses/python.png',
    '/images/about-us/courses/game-dev.png',
    '/images/about-us/courses/web-dev.png',
    '/images/about-us/courses/app-dev.png',
    '/images/about-us/courses/data-science.png',
    '/images/about-us/courses/roblox.png',
    '/images/about-us/courses/ap-cs-a.png',
    '/images/about-us/courses/ioi.png',
  ]

  return {
    label: course.label as LinguiJSMessageDescriptor,
    url: course.link,
    description: descriptions[index],
    image: images[index],
  }
})

const Courses = ({
  shouldScrollToTop,
}: {
  shouldScrollToTop?: boolean
}): JSX.Element => {
  const getRandomHoverBg = () => {
    const randomIndex = Math.floor(Math.random() * HOVER_BG.length)
    return HOVER_BG[randomIndex]
  }
  return (
    <div className="bg-white mx-auto px-5 py-12 lg:py-16">
      <div className="container mx-auto">
        <div className="text-center mb-10">
          <h2 className="max-w-4xl mx-auto pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
            Explore our courses
          </h2>
          <p className="text-lg max-w-4xl mx-auto">
            Coding, AI, Math, and Public Speaking courses to spark creativity
            and future-ready skills, with flexible self-paced paths for every
            learner.
          </p>
        </div>

        <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-1 sm:gap-4 mb-12">
          {COURSES.map((course, index) => (
            <Link key={index} href={course.url}>
              <a
                rel="noopener noreferrer"
                target="_blank"
                className={`md:w-56 md:h-56 flex flex-col items-center text-center p-1 sm:p-2 rounded-xl border border-grey-500 transition-all duration-300 group ${getRandomHoverBg()} hover:shadow-lg transition-all duration-300 no-underline cursor-pointer`}
              >
                <div className="w-24 h-24 flex items-center justify-center">
                  {course.image && (
                    <FastlyImage
                      src={course.image}
                      alt={msgT(course.label)}
                      width={96}
                      height={96}
                    />
                  )}
                </div>
                <h3 className="text-lg font-600 text-grey-900 mb-2 leading-none transition-all duration-300 group-hover:text-orange h-11">
                  <MsgTrans msg={course.label} />
                </h3>
                {course.description && (
                  <p className="text-sm text-grey-800 leading-tight">
                    {course.description}
                  </p>
                )}
              </a>
            </Link>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {shouldScrollToTop ? (
            <BookFreeClassButton
              variant="solid"
              intent="primary"
              size="lg"
              className="w-full sm:w-auto"
              shouldScrollToTop
            />
          ) : (
            <BookFreeClassButton
              variant="solid"
              intent="primary"
              size="lg"
              className="w-full sm:w-auto"
            />
          )}
          <Link href="/courses/">
            <a
              className="w-full sm:w-auto"
              rel="noopener noreferrer"
              target="_blank"
            >
              <Button
                variant="outline"
                intent="primary"
                size="lg"
                className="w-full sm:w-auto"
              >
                Browse all courses
              </Button>
            </a>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Courses
